import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import * as d3 from 'd3-selection'
import mousify from './mousify'
import leftClick from '../left-click'
import AutoScroll from './auto-scroll'
import intersection from 'path-intersection'
import KeyCodes from '../key-codes'
import validDrag from './valid-drag'

var MarqueeArea = Backbone.View.extend({
  className: 'marquee',

  initialize: function (options) {
    // The control point used for the marquee. It's where the original click started building the marquee.
    this.controlX = options.controlX
    this.controlY = options.controlY

    this.$el.css({
      left: this.controlX,
      top: this.controlY,
      position: 'absolute',
    })
    this.$el.css('z-index', 99999999)
  },

  area: function () {
    var parent = this.$el.parent()
      , area = this.$el.position()

    area.top += parent.scrollTop()
    area.left += parent.scrollLeft()
    area.height = this.$el.height()
    area.width = this.$el.width()
    return area
  },

  moveBy: function (dx, dy) {
    var left = parseInt(this.$el.css('left'), 10) + dx
      , top = parseInt(this.$el.css('top'), 10) + dy

    this.controlX = this.positiveX ? left : left + this.$el.outerWidth()
    this.controlY = this.positiveY ? top : top + this.$el.outerHeight()

    this.$el.css({left, top})
  },

  resize: function (left, top) {
    this.positiveX = true
    this.positiveY = true

    var dimensions = {
      width: left - this.controlX,
      height: top - this.controlY
    }

    if (dimensions.width < 0) {
      this.positiveX = false
      dimensions.left = this.controlX + dimensions.width
      dimensions.width *= -1
    }

    if (dimensions.height < 0) {
      this.positiveY = false
      dimensions.top = this.controlY + dimensions.height
      dimensions.height *= -1
    }
    this.$el.css(dimensions)
  }
})

var Marquee = Backbone.View.extend({
  events: {
    'mousedown': 'listen'
  },

  initialize: function (options) {
    _.bindAll(this, 'drag', 'drop', 'onScroll')
    this.canvas = options.canvas

    this.scroller = new AutoScroll({
      x: true,
      y: true,
      step: 10,
      threshold: 1
    }, options.canvas, this.onScroll)
  },

  onScroll: function () {
    // Fake a drag event
    this.drag({
      pageX: this.scroller._mouseCoords.x,
      pageY: this.scroller._mouseCoords.y
    })
  },

  listen: _.wrap(function (e) {
    if (this.canvas.$el.hasClass('is-dragging')) {
      // If the user is dragging wobs, we don't want to build a marquee.
      return
    }

    e.preventDefault() // Prevents the browser from trying to select text as the user is dragging the marquee

    // Make sure focus is not on an input element. Since we prevent default, the browser might keep
    // focus in an `input` if it has focus when the user starts the marquee.
    // See canvas #19870 and scoreboard #22816
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur()
    }

    this.offset = this.$el.offset()
    this.offset.left -= this.$el.scrollLeft()
    this.offset.top -= this.$el.scrollTop()

    let event = mousify(e)
      , self = this

    // Store details about the initial click
    this._initialClick = {
      ms: performance.now(), // ms for click
      x: event.pageX, // Coords
      y: event.pageY
    }

    this.scroller.start()
    this.scroller.mouse({
      x: event.pageX,
      y: event.pageY
    })

    let scrollContainer = this.canvas.getParent().get(0)

    this._initialScroll = {
      x: scrollContainer.scrollLeft,
      y: scrollContainer.scrollTop
    }

    if (this.marqueeArea != null) {
      this.marqueeArea.remove()
    }

    // Starting to build a marquee. The user may grab connectors. Let's cache all the points
    // That make up the connectors, so checking if a connector is inside of the marquee is faster
    this.connectors = this.canvas.connectorsContainer.selectAll('g.connector')
                                                     .nodes()
                                                     .map(node => d3.select(node).datum())

    $(document).on('mousemove.marquee', this.drag)
    $(document).on('mouseup.marquee', this.drop)
    $(document).on('keydown.marquee', e => {
      if (e.which == KeyCodes.SPACE) {
        // They are holding the space key. Next drag event will move the marquee rather than resize it
        this._moving = true
        e.preventDefault()
      }
    })
    $(document).on('keyup.marquee', (e) => {
      this._moving = false
    })
    this.$el.addClass('has-marquee')
    this.$el.trigger('start:marquee')
  }, leftClick),

  drag: function (e) {
    var self = this
      , event = mousify(e)

    if (!validDrag(this._initialClick, {x: e.pageX, y: e.pageY})) {
      // Not valid to start the marquee.
      return
    }

    if (!this.marqueeArea) {
      // First time we're showing the marquee area
      let controlX = this._initialClick.x - this.offset.left + 1
        , controlY = this._initialClick.y - this.offset.top + 1

      this.marqueeArea = new MarqueeArea({controlX, controlY })
      this.marqueeArea.$el.addClass('marquee')
      this.$el.append(this.marqueeArea.el)

      // Clear the drag threshold object since we've passed the time and/or distance
      // thresholds
      this._initialClick = null
    }

    this.scroller.mouse(_.extend({}, {x: event.pageX, y: event.pageY}))

    let scrollContainer = this.canvas.getParent().get(0)
      , scrollX = scrollContainer.scrollLeft - this._initialScroll.x
      , scrollY = scrollContainer.scrollTop - this._initialScroll.y

    let x = e.pageX + scrollX
      , y = e.pageY + scrollY

    if (this._moving) {
      // The user is dragging and holding the space bar, we want to move the marquee rather than resize it.

      // Determine the delta since last move
      var dx = x - this._pageX
        , dy = y - this._pageY

      // Move the marquee
      this.marqueeArea.moveBy(dx, dy)
    } else {
      this.marqueeArea.resize(x- this.offset.left, y - this.offset.top)
    }

    // Store the current positions in case we need them to determine when the user started a `move marquee`
    this._pageX = e.pageX + this.scroller._autoScrolled.x
    this._pageY = e.pageY + this.scroller._autoScrolled.y

    var area = this.marqueeArea.area()

    area.top -= this.canvas.padding
    area.left -= this.canvas.padding

    // Add/removes wobs from the selection based on the marquee size
    this.canvas.collection.each(function (wob) {
      if (wob.inArea(area)) {
        self.canvas.selection.add(wob)
      } else {
        self.canvas.selection.remove(wob)
      }
    })

    this.canvas.connectorsSelection.clear()

    // The connectors are potentially within this marquee
    // Build a polygon to check if any of the points along connectors' path are inside
    let polygon = [
      [area.left, area.top],
      [area.left + area.width, area.top],
      [area.left + area.width, area.top + area.height],
      [area.left, area.top + area.height]
    ]

    // Turn the polygon into a path so we can use path-intersection to determine if the connector is inside
    let path = `
      M${polygon[0]}
      L${polygon[1]}
      L${polygon[2]}
      L${polygon[3]}Z
    `

    this.connectors?.forEach(connector => {
      if (connector.connector.get('readonly')) {
        // readonly connectors cannot be selected in a marquee
        return
      }

      // Build a bounding box of this connector
      const bounds = arr => [Math.min(...arr), Math.max(...arr)]
      const x = bounds([connector.from.intersection[0], connector.to.intersection[0], connector.control[0]])
      const y = bounds([connector.from.intersection[1], connector.to.intersection[1], connector.control[1]])
      let intersected = intersection(path, connector.path).length > 0 // Check if the connector path intersects the marquee

      if (!intersected) {
        // See if the x bounds are totally contained inside the polygon
        intersected = x.every(x => x >= polygon[0][0] && x <= polygon[1][0]) && y.every(y => y >= polygon[0][1] && y <= polygon[2][1])
      }

      if (intersected) {
        this.canvas.connectorsSelection.add(connector.connector)
      } else {
        this.canvas.connectorsSelection.remove(connector.connector)
      }
    })
  },

  drop: function (e) {
    this.scroller.stop()

    // SpiderStrategies/Scoreboard#25075
    //
    // If the user just clicks on the canvas (instead of click/drag),
    // there's a good chance that:
    //  1. The marquee is created on mousedown
    //  2. The MarqueeArea gets under the mouse
    //  3. The mouseup event now goes on the MarqueeArea element, and
    //     the MarqueeArea is destroyed
    //  4. The click event goes on the MarqueeArea element, but since
    //     it's no longer in the DOM, it never bubbles up
    //  5. A document click event handler in Scoreboard never gets
    //     the click event
    //
    //  By destroying the MarqueeArea in nextTick(), the click event
    //  is able to bubble up through the DOM before the element is
    //  destroyed. This is ugly, but the best we could come up with.
    //
    //  Note that canvas.onPaint() would have been cleaner, but that
    //  didn't seem to be delayed long enough for the click to bubble.
    process.nextTick(() => {
      if (this.marqueeArea != null) {
        this.marqueeArea.remove()
      }
      this.$el.removeClass('has-marquee')
      this.$el.trigger('end:marquee')
      this.connectors = null
      this.marqueeArea = null
      $(document).off('.marquee')
    })
  },

  release: function () {
    if (this.marqueeArea != null) {
      this.marqueeArea.remove()
    }
  }
})

export default function (canvas) {
  return new Marquee({canvas: canvas, el: canvas.el})
}
